import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import '../assets/module-preview-viewport.css';

type PreviewDevice = 'desktop' | 'tablet' | 'mobile';
const devices: { device: PreviewDevice; label: string; icon: string }[] = [
    { device: 'desktop', label: 'Desktop', icon: 'pi pi-desktop' },
    { device: 'tablet', label: 'Tablet', icon: 'pi pi-tablet' },
    { device: 'mobile', label: 'Mobile', icon: 'pi pi-mobile' }
];

export function ModulePreviewViewport({ children, footer, enabled = true, onWidthChange, controlsTarget, onViewportScroll,
    viewportLabel = 'Module preview viewport', compactControls = false, preserveFocus = false, onViewportCommand, onCheckLayout }: {
    children: ReactNode;
    footer?: ReactNode;
    enabled?: boolean;
    onWidthChange?: (width: number | null) => void;
    controlsTarget?: HTMLElement | null;
    onViewportScroll?: () => void;
    viewportLabel?: string;
    compactControls?: boolean;
    preserveFocus?: boolean;
    onViewportCommand?: () => void;
    onCheckLayout?: () => void;
}) {
    const menuRef = useRef<Menu>(null);
    const menuId = useId();
    const [device, setDevice] = useState<PreviewDevice>('desktop');
    const [landscape, setLandscape] = useState(false);
    const width = device === 'mobile' ? (landscape ? 844 : 390)
        : device === 'tablet' ? (landscape ? 1024 : 768) : null;

    useEffect(() => { onWidthChange?.(enabled ? width : null); }, [enabled, width, onWidthChange]);

    if (!enabled) return <>{children}{footer}</>;

    const currentDevice = devices.find(option => option.device === device)!;
    const sizeLabel = width === null ? 'Available width' : `${width}px · ${landscape ? 'Landscape' : 'Portrait'}`;
    const controls = <div className={`module-device-controls${compactControls ? ' module-device-controls-compact' : ''}`} role="group" aria-label="Preview device"
            onMouseDown={event => { if (preserveFocus) event.preventDefault(); }}>
            {compactControls ? <>
                <Menu ref={menuRef} id={menuId} popup popupAlignment="right" className="module-device-menu"
                    appendTo={() => (document.fullscreenElement as HTMLElement | null) ?? document.body}
                    model={[
                        ...devices.map(option => ({ label: option.label, icon: device === option.device ? 'pi pi-check' : option.icon,
                            command: () => { setDevice(option.device); onViewportCommand?.(); } })),
                        { separator: true },
                        { label: 'Rotate preview', icon: 'pi pi-refresh', disabled: device === 'desktop',
                            command: () => { setLandscape(value => !value); onViewportCommand?.(); } },
                        { label: sizeLabel, disabled: true },
                        ...(onCheckLayout ? [{ separator: true }, { label: 'Check layout', icon: 'pi pi-check-square', command: onCheckLayout }] : [])
                    ]} />
                <Button type="button" icon={currentDevice.icon}
                    className="p-button-sm p-button-outlined module-device-menu-button"
                    aria-label="Device preview settings" aria-haspopup="menu" aria-controls={menuId}
                    title={`${currentDevice.label}: ${sizeLabel}`} onClick={event => menuRef.current?.toggle(event)} />
            </> : <>
            {devices.map(option => <Button key={option.device} type="button" label={option.label} icon={option.icon}
                aria-label={option.label} title={option.label}
                aria-pressed={device === option.device}
                className={`p-button-sm ${device === option.device ? '' : 'p-button-outlined'}`}
                onClick={() => setDevice(option.device)} />)}
            <Button type="button" label="Rotate" title="Rotate preview" icon="pi pi-refresh" className="p-button-sm p-button-text"
                disabled={device === 'desktop'} aria-label="Rotate preview" onClick={() => setLandscape(value => !value)} />
            </>}
            <span className={`module-device-width${compactControls ? ' module-device-width-hidden' : ''}`} role="status" aria-live="polite">
                {sizeLabel}
            </span>
        </div>;

    return <div className="module-device-preview">
        {controlsTarget === undefined ? controls : controlsTarget && createPortal(controls, controlsTarget)}
        <div className={`module-device-stage${width === null ? '' : ' module-device-stage-framed'}`} onScroll={onViewportScroll}>
            <div className="module-device-screen" data-preview-device={device}
                aria-label={viewportLabel} style={{ width: width === null ? '100%' : width }}>
                <div className="module-device-content">{children}</div>
                {footer && <div className="module-device-footer">{footer}</div>}
            </div>
        </div>
    </div>;
}
